/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { CommonModule } from '@angular/common';
import { Component, input, computed, HostBinding, inject, effect, ViewEncapsulation, Host } from '@angular/core';
import { RouterLink } from '@angular/router';

import { ENVIRONMENT } from '@environment';
import { ChannelService } from '@usheru-hff/sdk/channel';
import {
  MovieSiteImageConfig,
  ImageConfigBaseType,
  MovieSiteChannelLogo,
  MovieSiteCustomImage,
  TreatmentConfig
} from '@usheru-hff/sdk/movie-site/movie-site-image-config.model';
import { Movie } from '@usheru-hff/shared/domain';

import { MovieSiteConfigurationService } from '../movie-site-configuration.service';

const INITIAL_BACKGROUND = 'unset';
@Component({
  standalone: true,
  imports: [CommonModule, RouterLink],
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'movie-site-logo-image',
  templateUrl: './movie-site-logo-image.component.html',
  styleUrls: ['./movie-site-logo-image.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MovieSiteLogoImageComponent {
  readonly env = inject(ENVIRONMENT);
  readonly configService = inject(MovieSiteConfigurationService);
  readonly channelService = inject(ChannelService);

  @HostBinding('class.movie-site-logo-image') className = true;
  @HostBinding('attr.data-type') dataType: ImageConfigBaseType | undefined = undefined;
  @HostBinding('attr.data-no-logo-status') noLogoStatus: true | null = null;

  @HostBinding('style.--movie-site-logo-background-large') backgroundLarge = INITIAL_BACKGROUND;
  @HostBinding('style.--movie-site-logo-background-small') backgroundSmall = INITIAL_BACKGROUND;
  @HostBinding('style.--movie-site-logo-background') background = INITIAL_BACKGROUND;
  @HostBinding('style.--movie-site-logo-height__config') logoHeight = null;

  @HostBinding('attr.data-drop-shadow-black')
  get dropShadowBlack() {
    const image = this.image();
    const treatment = this.image() as TreatmentConfig;
    if (image.type === 'channel-logo') {
      const chTreatment = this.configService.mainLogoTreatment();
      if (chTreatment?.treatmentWhenContrastBlack)
        treatment.treatmentWhenContrastBlack = chTreatment.treatmentWhenContrastBlack;
    }
    return treatment.treatmentWhenContrastBlack === 'drop-shadow' && this.contrast() === 'black' ? true : null;
  }

  @HostBinding('attr.data-drop-shadow-white')
  get dropShadowWhite() {
    const image = this.image();
    const treatment = this.image() as TreatmentConfig;
    if (image.type === 'channel-logo') {
      const chTreatment = this.configService.mainLogoTreatment();
      if (chTreatment?.treatmentWhenContrastWhite)
        treatment.treatmentWhenContrastWhite = chTreatment.treatmentWhenContrastWhite;
    }
    return treatment.treatmentWhenContrastWhite === 'drop-shadow' && this.contrast() === 'white' ? true : null;
  }

  readonly image = input.required<MovieSiteImageConfig>();
  /**
   * Important to determinate the treatment to apply to a logo image.
   *
   * Check the **contrastMap** in the MovieSiteConfigurationService to get the correct value.
   */
  readonly contrast = input<'black' | 'white' | undefined>(undefined);

  readonly movie = input<Movie | undefined>(undefined);

  readonly height = input<string | null>(null);
  readonly logoFilter = input<string | null>(null);

  readonly channel = this.channelService.channel;
  /**
   * Apps with only movies do not have url linking to home page.
   */
  readonly isOnlyMovieApp = this.env.appHasOnlyMovies || false;

  readonly logoStyling = computed(() => this.setLogoStyling());

  readonly customImage = computed(() => {
    const img = this.image() as MovieSiteCustomImage;
    const treatmentBlack = (img as TreatmentConfig).treatmentWhenContrastBlack;
    const treatmentWhite = (img as TreatmentConfig).treatmentWhenContrastWhite;
    // Detects if the treatment is a drop-shadow or a custom url to load the alternative image
    if (this.contrast() === 'black' && treatmentBlack?.startsWith('http')) {
      img.src = treatmentBlack;
    } else if (this.contrast() === 'white' && treatmentWhite?.startsWith('http')) {
      img.src = treatmentWhite;
    }
    return img;
  });
  readonly isCustomImageUrlExternal = computed(() => this.customImage()?.url?.startsWith('http'));

  readonly channelImage = computed(() => this.image() as MovieSiteChannelLogo);
  readonly channelName = this.channel?.name;
  readonly movieLogo = computed(() => this.movie()?.logoUrl);
  readonly movieLogoAlt = computed(() => `${this.movie()?.title} logo`);

  readonly mainLogoUrl = computed(() => {
    const contrast = this.contrast();
    const chTreatment = this.configService.mainLogoTreatment();
    const chTreatmentBlack = chTreatment?.treatmentWhenContrastBlack;
    const chTreatmentWhite = chTreatment?.treatmentWhenContrastWhite;
    if (contrast === 'black' && chTreatmentBlack?.startsWith('http')) {
      return chTreatmentBlack;
    } else if (contrast === 'white' && chTreatmentWhite?.startsWith('http')) {
      return chTreatmentWhite;
    }
    return this.channel?.logoUrl || this.channel?.logoSmall || null;
  });

  readonly disableChannelLogoInteraction = computed(() => {
    if (this.image().type === 'channel-logo') {
      return this.configService.logosConfiguration()?.mainLogo?.disableInteraction || false;
    }
    return false;
  });

  constructor() {
    effect(() => {
      this.setDateType(this.image().type);
      this.setDisplay(this.image());
    });

    effect(() => {
      this.logoHeight = this.height() || null;
    });
  }

  private setDateType(type: ImageConfigBaseType) {
    this.dataType = type;
  }

  /**
   *  Hide the logo image if the channel logo is not set or the movie logo is not set
   */
  private setDisplay(image: MovieSiteImageConfig) {
    if (image.type === 'channel-logo' && !this.mainLogoUrl) {
      this.noLogoStatus = true;
    } else if (image.type === 'movie-logo' && !this.movieLogo()) {
      this.noLogoStatus = true;
    } else {
      this.noLogoStatus = null;
    }
  }

  /**
   * Return additional styling for the channel logo.
   */
  private setLogoStyling() {
    let dropShadow: string | null = null;
    const dropShadowConfig = this.image().shadow;
    if (dropShadowConfig) {
      dropShadow = `${dropShadowConfig.color || 'rgba(0, 0, 0, 0.5)'} ${dropShadowConfig.offsetX || '0px'} ${dropShadowConfig.offsetY || '0px'} ${dropShadowConfig.blur || 10}px`;
    }
    const extraFilter = this.logoFilter();
    this.background = this.image().background || null;
    this.backgroundLarge = this.image().backgroundLargeScreen;
    this.backgroundSmall = this.image().backgroundSmallScreen;
    const dropShadowFilter = dropShadow ? `drop-shadow(${dropShadow})` : null;
    const filter =
      extraFilter && dropShadowFilter ? `${extraFilter} ${dropShadowFilter}` : extraFilter || dropShadowFilter || null;

    return {
      padding: this.image().padding || null,
      'object-fit': this.image().fit || 'contain',
      filter
    };
  }

  get mainLogoAlt() {
    return `${this.channelName} logo`;
  }
}
